import { Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { TemplateRef, ViewContainerRef } from '@angular/core';
import { TcsLoadingService } from './loading.service';
import { ILoadingRef } from './loading.factory';
import { LoadingMode, LoadingStrategy, LoadingType } from './components/loading/loading.component';

/**
 * Context class for variable reference
 */
export class TcsLoadingContext {
  public $implicit: any = undefined;
  public tcsLoading: any = undefined;
}

// Constant for generation of the id for the next component
let TCS_LOADING_NEXT_ID = 0;

@Directive({
  selector: '[tcsLoading]',
})
export class TcsLoadingDirective implements OnInit, OnDestroy {
  private _context: TcsLoadingContext = new TcsLoadingContext();
  private _loadingRef?: ILoadingRef;

  /**
   * tcsLoading: string
   * Name reference of the loading mask, used to register/resolve requests to the mask.
   */
  @Input('tcsLoading')
  name!: string;

  /**
   * tcsLoadingUntil?: any
   * If its null, undefined or false it will be used to register requests to the mask.
   * Else if its any value that can be resolved as true, it will resolve the mask.
   * [name] is optional when using [until], but can still be used to register/resolve it manually.
   */
  @Input('tcsLoadingUntil')
  set until(until: any) {
    if (!this.name) {
      this.name = 'tcs-loading-until-' + TCS_LOADING_NEXT_ID++;
    }
    this._context.$implicit = this._context.tcsLoading = until;
    if (until === false) {
      this._loadingService.register(this.name);
    } else {
      this._loadingService.resolveAll(this.name);
    }
  }

  /**
   * tcsLoadingType?: LoadingType or ['linear' | 'circular']
   * Sets the type of loading mask depending on value.
   * Defaults to [LoadingType.Circular | 'circular'].
   */
  @Input('tcsLoadingType')
  type: LoadingType = LoadingType.Circular;

  /**
   * tcsLoadingMode?: LoadingMode or ['determinate' | 'indeterminate']
   * Sets the mode of loading mask depending on value.
   * Defaults to [LoadingMode.Indeterminate | 'indeterminate'].
   */
  @Input('tcsLoadingMode') mode: LoadingMode = LoadingMode.Indeterminate;

  /**
   * tcsLoadingStrategy?: LoadingStrategy or ['replace' | 'overlay']
   * Sets the strategy of loading mask depending on value.
   * Defaults to [LoadingMode.Replace | 'replace'].
   */
  @Input('tcsLoadingStrategy')
  strategy: LoadingStrategy.Overlay | LoadingStrategy.Replace = LoadingStrategy.Replace;

  /**
   * tcsLoadingColor?: "primary" | "accent" | "warn"
   * Sets the theme color of the loading component. Defaults to "primary"
   */
  @Input('tcsLoadingColor') color: 'primary' | 'accent' | 'warn' = 'primary';

  constructor(
    private _viewContainerRef: ViewContainerRef,
    private _templateRef: TemplateRef<TcsLoadingContext>,
    private _loadingService: TcsLoadingService,
  ) {}

  /**
   * Registers component in the DOM, so it will be available when calling resolve/register.
   */
  ngOnInit(): void {
    this._registerComponent();
  }

  /**
   * Remove component when directive is destroyed.
   */
  ngOnDestroy(): void {
    this._loadingService.removeComponent(this.name);
    this._loadingRef = undefined;
  }

  /**
   * Creates [TcsLoadingComponent] and attaches it to this directive's [ViewContainerRef].
   * Passes this directive's [TemplateRef] to modify DOM depending on loading `strategy`.
   */
  private _registerComponent(): void {
    if (!this.name) {
      throw new Error('Name is needed to register loading directive');
    }
    // Check if `TcsLoadingComponent` has been created before trying to add one again.
    // There is a weird edge case when using `[routerLinkActive]` that calls the `ngOnInit` twice in a row
    if (this._loadingRef == null) {
      this._loadingRef = this._loadingService.createComponent(
        {
          name: this.name,
          type: this.type,
          mode: this.mode,
          color: this.color,
          strategy: this.strategy,
        },
        this._viewContainerRef,
        this._templateRef,
        this._context,
      );
    }
  }
}
